/**
 * SHARED VALIDATOR ENGINE
 * Single entry point for schedule validation used by BOTH paths:
 *   - Make Remittances modal  (workers/excel.worker.js)
 *   - Schedule Validator tool (ExcelValidatorTool/index.tsx)
 *
 * Guarantees the two paths can never drift:
 *   1. normalizeScheduleRows() canonicalizes row keys against scheduleHeaders
 *      so cross-field rules can never be silently skipped because the uploaded
 *      file spelled its headers differently (e.g. "FOR THE MONTH OF").
 *   2. validateScheduleRows() is the ONLY place that builds/runs
 *      field + row + global validations (from shared/validationRules.ts).
 */

import * as XLSX from 'xlsx';
import { canonicalHeaderLookup, scheduleHeaders } from './scheduleHeaders';
import {
  createValidationRules,
  createRowValidations,
  createGlobalValidations,
} from './validationRules';
import type {
  FieldDefinition,
  ValidationError,
  ValidationResult,
} from '../ExcelValidatorTool/lib/types';

/**
 * Canonicalizes raw parsed rows:
 * - Maps each row key to its canonical scheduleHeaders spelling
 *   (case-insensitive / trimmed match; unknown keys are kept trimmed as-is).
 * - Converts every value to a trimmed string ('' for null/undefined).
 *
 * MUST be called on any data before validateScheduleRows().
 */
export function normalizeScheduleRows(
  rawRows: Record<string, any>[]
): Record<string, any>[] {
  return rawRows.map(row => {
    const normalized: Record<string, any> = {};
    for (const key in row) {
      if (!Object.prototype.hasOwnProperty.call(row, key)) continue;
      const trimmedKey = key.trim();
      const canonicalKey =
        canonicalHeaderLookup.get(trimmedKey.toLowerCase()) || trimmedKey;
      const value = row[key];

      // When xlsx parses a numeric cell it gives us a raw JS float (e.g.
      // 5000.000000000001) rather than the 2-dp display the user sees in
      // Excel. String(float) preserves all that noise and then fails the
      // ≤2 decimal-place regex even though the amount is valid.
      // Fix: round finite numbers to 2dp and drop trailing zeros before
      // stringifying, so the validator always sees a clean decimal string.
      let strValue: string;
      if (typeof value === 'number' && isFinite(value)) {
        strValue = parseFloat(value.toFixed(2)).toString();
      } else {
        strValue = value === null || value === undefined ? '' : String(value).trim();
      }

      normalized[canonicalKey] = strValue;
    }
    return normalized;
  });
}

/**
 * Reads the header names from an XLSX worksheet's header row.
 */
export function extractWorksheetHeaderNames(
  worksheet: XLSX.WorkSheet,
  headerRowIndex = 0
): string[] {
  if (!worksheet['!ref']) return [];
  const range = XLSX.utils.decode_range(worksheet['!ref']);
  const headerRow = range.s.r + headerRowIndex;
  const headerNames: string[] = [];
  for (let C = range.s.c; C <= range.e.c; ++C) {
    const cell = worksheet[XLSX.utils.encode_cell({ c: C, r: headerRow })];
    if (cell && cell.v) {
      headerNames.push(String(cell.v).trim());
    }
  }
  return headerNames;
}

/**
 * Validates that all field labels are present in the uploaded header names.
 * Matching is case-insensitive and whitespace tolerant.
 */
export function validateHeaderNames(
  headerNames: string[],
  fields: FieldDefinition[]
): { isValid: boolean; missingHeaders: string[]; errorMessage?: string } {
  const foundHeaders = new Set(
    headerNames.filter(Boolean).map(h => h.trim().toLowerCase())
  );

  const missingHeaders: string[] = [];
  for (const field of fields) {
    const labelLower = field.label.trim().toLowerCase();
    const keyLower = field.key.trim().toLowerCase();
    if (!foundHeaders.has(labelLower) && !foundHeaders.has(keyLower)) {
      missingHeaders.push(field.label.trim());
    }
  }

  if (missingHeaders.length > 0) {
    return {
      isValid: false,
      missingHeaders,
      errorMessage: `The uploaded file is missing required headers: ${missingHeaders.join(', ')}. Please download the template and use the correct format.`,
    };
  }

  return { isValid: true, missingHeaders: [] };
}

/**
 * Maps field.key -> column index by matching worksheet header cells to fields
 * (case-insensitive). Used to compute Excel cell references for error reporting.
 */
export function buildColumnIndexMap(
  worksheet: XLSX.WorkSheet,
  fields: FieldDefinition[],
  headerRowIndex = 0
): Map<string, number> {
  const columnMap = new Map<string, number>();
  if (!worksheet['!ref']) return columnMap;

  const range = XLSX.utils.decode_range(worksheet['!ref']);
  const headerRow = range.s.r + headerRowIndex;

  for (let C = range.s.c; C <= range.e.c; ++C) {
    const cell = worksheet[XLSX.utils.encode_cell({ c: C, r: headerRow })];
    if (!cell || !cell.v) continue;
    const headerValue = String(cell.v).trim();
    const field = fields.find(f => {
      const fieldLabel = f.label.trim();
      const fieldKey = f.key.trim();
      return (
        fieldLabel === headerValue ||
        fieldKey === headerValue ||
        fieldLabel.toLowerCase() === headerValue.toLowerCase() ||
        fieldKey.toLowerCase() === headerValue.toLowerCase()
      );
    });
    if (field && !columnMap.has(field.key)) {
      columnMap.set(field.key, C);
    }
  }
  return columnMap;
}

const resolveCellValue = (
  row: Record<string, any>,
  field: FieldDefinition
): any => {
  const trimmedFieldKey = field.key.trim();
  const trimmedFieldLabel = field.label.trim();

  if (row.hasOwnProperty(trimmedFieldKey)) return row[trimmedFieldKey];
  if (row.hasOwnProperty(trimmedFieldLabel)) return row[trimmedFieldLabel];
  if (row.hasOwnProperty(field.key)) return row[field.key];
  if (row.hasOwnProperty(field.label)) return row[field.label];

  // Case-insensitive last resort
  const rowKey = Object.keys(row).find(
    key => key.trim().toLowerCase() === trimmedFieldKey.toLowerCase()
  );
  return rowKey ? row[rowKey] : undefined;
};

const validateCell = (
  value: any,
  field: FieldDefinition,
  row: Record<string, any>,
  allRows: Record<string, any>[],
  rowIndex: number
): string | null => {
  const processedValue = value === null || value === undefined ? '' : value;
  const stringValue = String(processedValue).trim();

  for (const validation of field.validations) {
    switch (validation.rule) {
      case 'required':
        if (processedValue === '' || stringValue === '') {
          return validation.errorMessage;
        }
        break;

      case 'regex': {
        const regex =
          typeof validation.value === 'string'
            ? new RegExp(validation.value)
            : validation.value instanceof RegExp
              ? validation.value
              : null;
        if (regex && stringValue && !regex.test(stringValue)) {
          return validation.errorMessage;
        }
        break;
      }

      case 'custom':
        if (typeof validation.value === 'function') {
          const error = validation.value(value, row, allRows);
          if (error) return error;
        }
        break;
    }
  }

  return null;
};

export interface ValidateScheduleRowsOptions {
  /** Rows MUST already be canonicalized via normalizeScheduleRows() */
  rows: Record<string, any>[];
  pfaCodes?: string[];
  employerCode?: string;
  /** Optional field-key -> column index map for Excel cell refs in errors */
  columnIndices?: Map<string, number>;
  headerRowIndex?: number;
}

/**
 * THE single validation entry point.
 * Runs field-level, row-level and global-level validations over canonical rows.
 */
export function validateScheduleRows({
  rows,
  pfaCodes = [],
  employerCode,
  columnIndices,
  headerRowIndex = 0,
}: ValidateScheduleRowsOptions): ValidationResult {
  const fields = createValidationRules(pfaCodes);
  const rowValidations = createRowValidations();
  const globalValidations = createGlobalValidations(employerCode);
  const errors: ValidationError[] = [];

  rows.forEach((row, rowIndex) => {
    // Field-level validations
    fields.forEach(field => {
      let value = resolveCellValue(row, field);
      if (value === undefined || value === null) value = '';

      const errorMessage = validateCell(value, field, row, rows, rowIndex);
      if (errorMessage) {
        const columnIndex =
          columnIndices?.get(field.key) ?? columnIndices?.get(field.label) ?? 0;
        errors.push({
          cellRef: XLSX.utils.encode_cell({
            c: columnIndex,
            r: rowIndex + headerRowIndex + 1,
          }),
          columnKey: field.key,
          rowIndex,
          errorMessage,
          cellValue: value,
        });
      }
    });

    // Row-level validations
    rowValidations.forEach(validateRow => {
      const rowErrors = validateRow(row, rowIndex, rows);
      rowErrors.forEach(error => {
        if (!error.cellRef) {
          const columnIndex = columnIndices?.get(error.columnKey) ?? 0;
          error.cellRef = XLSX.utils.encode_cell({
            c: columnIndex,
            r: rowIndex + headerRowIndex + 1,
          });
        }
        errors.push(error);
      });
    });
  });

  // Global-level validations
  globalValidations.forEach(validateGlobal => {
    const globalErrors = validateGlobal(rows);
    globalErrors.forEach(error => {
      if (!error.cellRef) {
        const columnIndex = columnIndices?.get(error.columnKey) ?? 0;
        error.cellRef = XLSX.utils.encode_cell({
          c: columnIndex,
          r: error.rowIndex + headerRowIndex + 1,
        });
      }
      errors.push(error);
    });
  });

  return {
    isValid: errors.length === 0,
    errors,
    data: rows,
    errorCount: errors.length,
  };
}

// Re-exported so callers never need to import rule builders directly,
// keeping one import surface for both paths.
export { scheduleHeaders };
